// src/components/HtmlResumeDisplay.js
/**
 * HtmlResumeDisplay.js
 *
 * A component that displays and allows editing of resume content in HTML format
 * with the ability to download as a Word document
 */

import React, { useState, useEffect, useRef } from "react";
import { Box, Snackbar, makeStyles } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";

import { EnhancedHtmlEditor } from "./HtmlEditor";
import { generateResumeHtml, getTemplates } from "./ResumeHtmlGenerator";
import adaptFormDataForTemplates from "./ResumeDataAdapter";
import {
  extractContentBySections,
  mergeContentIntoTemplate,
} from "./HtmlEditor/utils";

// Alert component for notifications
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Styles
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    overflow: "hidden", // Prevent horizontal scrolling
    [theme.breakpoints.down(1416)]: {
      // Add responsive styles for smaller screens
      padding: 0,
    },
  },
}));

/**
 * HtmlResumeDisplay Component
 *
 * A WYSIWYG editor for resumes that uses HTML directly
 *
 * @param {Object} props - Component props
 * @param {Object} props.resumeData - The structured resume data
 * @param {Array} props.keywords - Keywords for highlighting
 * @param {Function} props.onContentChange - Callback when content changes
 * @param {Function} props.onScrollToATS - Callback to scroll to ATS Analysis section
 * @returns {JSX.Element} The rendered component
 */
const HtmlResumeDisplay = ({
  resumeData,
  keywords = [],
  onContentChange,
  onScrollToATS,
}) => {
  const classes = useStyles();
  const [html, setHtml] = useState("");
  const [editorKey, setEditorKey] = useState(Date.now()); // Key to force editor remount
  const [selectedTemplate, setSelectedTemplate] = useState("standard");
  const [templates, setTemplates] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Reference to track previous skills for comparison
  const prevSkillsRef = useRef(null); // Initialize to null

  // Load available templates
  useEffect(() => {
    const availableTemplates = getTemplates();
    console.log("Available templates:", availableTemplates);
    setTemplates(availableTemplates);
  }, []);

  // Generate/Update HTML when resumeData or selectedTemplate changes
  useEffect(() => {
    if (resumeData) {
      console.log(
        "HtmlResumeDisplay useEffect triggered. Template:",
        selectedTemplate
      );

      const currentSkillsString = resumeData.skills
        ? JSON.stringify(resumeData.skills)
        : null;
      const previousSkillsString = prevSkillsRef.current;
      const skillsJustChanged = currentSkillsString !== previousSkillsString;

      // Determine if we need to update the HTML state
      // Update if:

      // Initial HTML generation or template change
      if (!html || selectedTemplate !== prevTemplateRef.current) {
        console.log(
          "Generating initial HTML or applying template change. Template:",
          selectedTemplate
        );
        const adaptedResumeData = adaptFormDataForTemplates(resumeData);
        const newGeneratedHtml = generateResumeHtml(
          adaptedResumeData,
          selectedTemplate
        );
        setHtml(newGeneratedHtml);
        prevTemplateRef.current = selectedTemplate; // Update template ref
        // Don't call onContentChange here for initial generation
      }
      // Handle external skills update by regenerating HTML and forcing remount
      else if (skillsJustChanged) {
        console.warn(
          "Skills data changed externally. Regenerating HTML and forcing editor remount."
        );
        console.log("Old skills ref:", previousSkillsString);
        console.log("New skills data:", currentSkillsString);

        // Regenerate HTML from the latest resumeData
        const adaptedResumeData = adaptFormDataForTemplates(resumeData);
        const newGeneratedHtml = generateResumeHtml(
          adaptedResumeData,
          selectedTemplate
        );

        // Update the key to force remount BEFORE setting the new HTML
        setEditorKey(Date.now());
        setHtml(newGeneratedHtml); // Set the new HTML

        // *** DO NOT call onContentChange here for external data updates ***
      }

      // Update the skills ref *after* processing potential changes
      prevSkillsRef.current = currentSkillsString;
    }
    // Dependencies: Run only when resumeData or selectedTemplate props change.
    // DO NOT include html or onContentChange here to avoid loops on external updates.
  }, [resumeData, selectedTemplate]);

  // Ref to track previous template
  const prevTemplateRef = useRef(selectedTemplate);

  // Handle content changes from the editor with improved template switching
  const handleContentChange = ({ htmlContent, template }) => {
    console.log(
      "handleContentChange called with template:",
      template,
      "current template:",
      selectedTemplate
    );

    // If template has changed, apply the new template's styling while preserving edits
    if (template && template !== selectedTemplate) {
      console.log("Template change detected, applying new template styling");

      // Store the current template before changing it
      const oldTemplate = selectedTemplate;

      // Update the template state
      setSelectedTemplate(template);

      // Apply the new template's styling while preserving user edits
      if (resumeData) {
        try {
          // 1. Extract user content from the current HTML
          const userContent = extractUserContent(htmlContent);

          // 2. Generate fresh HTML with the new template
          const adaptedResumeData = adaptFormDataForTemplates(resumeData);
          const templateHtml = generateResumeHtml(adaptedResumeData, template);

          // 3. Merge user content into the new template HTML
          const mergedHtml = mergeUserContentIntoTemplate(
            templateHtml,
            userContent
          );

          // 4. Update the HTML with the merged content
          setHtml(mergedHtml);

          // Show success feedback
          setSnackbar({
            open: true,
            message: `Template changed to ${template}. Your edits have been preserved.`,
            severity: "success",
          });

          // Notify parent of changes
          if (onContentChange) {
            onContentChange({ htmlContent: mergedHtml, template });
          }
          return; // Skip the normal update below
        } catch (error) {
          console.error("Error applying template:", error);
          // If merging fails, fall back to just updating the template
          setSnackbar({
            open: true,
            message: `Changed to ${template} template, but couldn't apply all styling. Your edits are preserved.`,
            severity: "warning",
          });
        }
      }
    }

    // For normal content changes (not template changes)
    setHtml(htmlContent);

    // Always notify parent of changes
    if (onContentChange) {
      onContentChange({ htmlContent, template: template || selectedTemplate });
    }
  };

  // Helper function to extract user content from HTML
  const extractUserContent = (html) => {
    // Use the utility function to extract content by sections
    return extractContentBySections(html);
  };

  // Helper function to merge user content into template HTML
  const mergeUserContentIntoTemplate = (templateHtml, userContent) => {
    // Use the utility function to merge content into template
    return mergeContentIntoTemplate(templateHtml, userContent);
  };

  // Handle Word document download
  const handleDownloadWord = async ({ html, template, resumeData }) => {
    try {
      console.log(`Downloading Word document with template: ${template}`);

      // Call the cloud function to convert HTML to Word
      const response = await fetch(
        "https://html-to-docx-660323987151.us-central1.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            html,
            template, // Pass the template name to the cloud function
            firstName: resumeData?.personalInformation?.firstName || "",
            lastName: resumeData?.personalInformation?.lastName || "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate document");
      }

      // Get the response data which contains the base64 encoded document
      const responseData = await response.json();

      // Convert base64 to blob
      const byteCharacters = atob(responseData.docx);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: responseData.contentType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Use the filename from the response or generate one
      a.download =
        responseData.filename ||
        `${resumeData?.personalInformation?.firstName || "Resume"}_${
          resumeData?.personalInformation?.lastName || ""
        }_Resume.docx`.replace(/\s+/g, "_");

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      setSnackbar({
        open: true,
        message: "Resume downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading document:", error);

      // Show error message
      setSnackbar({
        open: true,
        message: "Error downloading resume. Please try again.",
        severity: "error",
      });

      // Re-throw the error to let the editor component know
      throw error;
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <EnhancedHtmlEditor
        key={editorKey} // Add the key prop here
        resumeData={resumeData}
        templates={templates}
        initialTemplate={selectedTemplate}
        html={html}
        keywords={keywords}
        onContentChange={handleContentChange}
        onDownload={handleDownloadWord}
        onScrollToATS={onScrollToATS}
        documentType="resume" // Specify document type
      />

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HtmlResumeDisplay;
